import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Select } from '@/components/Controls/Select';
import { Datatable } from '@/components/Datatable/Datatable';
import UserFilter from '@/components/Custom/Filter/UserFilter';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import { TableViewProps } from '@/type/listing.types';
import {
  getOptionsUserRoleFilter,
  getOptionsStatusFilter,
} from '@/utils/constant.utils';
import { mapFilterSelect } from '@/utils/map.utils';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { useUserRole } from '@/hooks/auth.hooks';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useUserFilter } from '@/hooks/filters/user-filter.hooks';

const limit = 20;

export const UserTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const {
    hasNoRole,
    isAdmin,
    isModerator,
    isGroupManager,
    isCompanyManager,
    isBranchAgent,
  } = useUserRole();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    role,
    status,
    groupId,
    companyId,
    branchId,
    debounceFilterUpdate,
    searchByStatus,
    searchByRolesTypes,
    searchByGroup,
    searchByCompany,
    searchByBranch,
  } = useUserFilter({ setCurrentPage });
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups();
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      groupId: debouncedFilter?.groupId,
    });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle textClassName="mt-1.5" text={t('common:users')} />
            {!hasNoRole && !isModerator && !isBranchAgent && (
              <div className="hidden max-md:block">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            )}
          </div>
          {!hasNoRole && !isModerator && !isBranchAgent && (
            <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
              <div className="flex items-center flex-1 gap-3.5 min-w-[300px] max-1.5xl:min-w-0 max-1.5xl:basis-full">
                <Input
                  placeholder={t('searchUser')}
                  type="text"
                  name="search"
                  autoComplete="off"
                  leftIcon={
                    <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                  }
                  inputWrapperClassName="w-full"
                  inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  value={key}
                  onChange={(e) => debounceFilterUpdate(e.target.value)}
                />
                <div className="hidden max-1.5xl:block max-md:hidden">
                  <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
                </div>
              </div>
              <Select
                containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
                selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={getOptionsUserRoleFilter(t)}
                value={role}
                onChange={(e) => searchByRolesTypes(e.target.value)}
              />
              <Select
                containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
                selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={getOptionsStatusFilter(t)}
                value={status}
                onChange={(e) => searchByStatus(e.target.value)}
              />
              <div className="flex justify-end max-sm:w-full">
                <ButtonAdvancedFilter>
                  <UserFilter
                    t={t}
                    isLoadingAllGroups={isLoadingAllGroups}
                    isLoadingAllCompanies={isLoadingAllCompanies}
                    isLoadingAllBranches={isLoadingAllBranches}
                    groupOptions={mapFilterSelect(allGroups?.data)}
                    companyOptions={mapFilterSelect(allCompanies?.data)}
                    branchOptions={mapFilterSelect(allBranches?.data)}
                    displayGroupFilter={
                      isAdmin || isModerator || isGroupManager
                    }
                    displayCompanyFilter={
                      isAdmin ||
                      isModerator ||
                      isGroupManager ||
                      isCompanyManager
                    }
                    displayBranchFilter
                    groupId={groupId}
                    companyId={companyId}
                    branchId={branchId}
                    searchByGroup={searchByGroup}
                    searchByCompany={searchByCompany}
                    searchByBranch={searchByBranch}
                  />
                </ButtonAdvancedFilter>
              </div>
              <div className="max-1.5xl:hidden">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            </div>
          )}
        </div>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
